import React, { type FC } from 'react';
import _ from 'lodash';
import { cx, hoc } from '#packages/util';
import { useTranslation } from 'react-i18next';
import withOpenDropPanel, {
  type WithOpenDropPanelProps,
} from '../hocs/withOpenDropPanel';
import {
  mapStateToProps,
  mapDispatchToProps,
  type ToolsButtonStateProps,
  type ToolsButtonDispatchProps,
  shouldShowToolsComponent,
} from './toolsMappers';
import TopBarButton from '../topBarButton/topBarButtonWithTooltip';
import TooltipContent from '../tooltipContent';
import DropDown from '../dropDown/dropDown';
import ToolsPanel from './toolsPanel';
import { Custom } from '@wix/wix-ui-icons-common/classic-editor';

const {
  STORES: { EDITOR_API },
  withConditionalRender,
} = hoc;

export interface ToolsButtonOwnProps {
  className?: string;
  delayedFadeOut?: boolean;
  disabled?: boolean;
}

interface ToolsButtonProps
  extends ToolsButtonOwnProps,
    ToolsButtonStateProps,
    ToolsButtonDispatchProps,
    WithOpenDropPanelProps {}

const ToolsButton: FC<ToolsButtonProps> = ({
  className,
  closeDropPanel,
  delayedFadeOut,
  isPanelOpen,
  openDropPanel,
  panelName,
  isZoomMode,
  isLabelShown,
  reportToolsClicked,
  disabled,
}) => {
  const { t } = useTranslation();

  const defaultTooltip =
    isPanelOpen || disabled ? null : (
      <TooltipContent
        tooltipData={{
          title: 'TOPBAR_TOOLS_TOOLTIP_TITLE',
          text: 'TOPBAR_TOOLS_TOOLTIP_TEXT',
        }}
      />
    );
  const zoomModeTooltip = (
    <TooltipContent
      tooltipData={{
        text: 'TOPBAR_ZoomOutSection_Tools_Disabled_Tooltip',
      }}
    />
  );

  const tooltip = isZoomMode ? zoomModeTooltip : defaultTooltip;

  const handleClick = () => {
    openDropPanel(panelName);
    reportToolsClicked();
  };

  return (
    <DropDown
      isOpen={isPanelOpen}
      delayedFadeOut={delayedFadeOut}
      panelContent={<ToolsPanel closePanel={closeDropPanel} />}
      onPanelOuterClick={() => {
        closeDropPanel();
      }}
      alignment="left"
    >
      <TopBarButton
        tooltip={tooltip}
        toggled={isPanelOpen}
        automationId="top-bar-tools-button"
        className={cx('top-bar-tools-btn', className, {
          'lower-position': true,
          minimized: !isLabelShown,
        })}
        customSymbol={<Custom className={'btn-symbol '} />}
        label={isLabelShown && t('Topbar_Tools_Label')}
        onClick={handleClick}
        disabled={disabled || isZoomMode}
        tooltipMaxWidth={230}
      />
    </DropDown>
  );
};

const ConnectedToolsButton = _.flow(
  hoc.connect(hoc.STORES.EDITOR_API, mapStateToProps, mapDispatchToProps),
  withOpenDropPanel,
  withConditionalRender(EDITOR_API, shouldShowToolsComponent),
)(ToolsButton) as React.ComponentType;

(ConnectedToolsButton as AnyFixMe).pure = ToolsButton;

export default ConnectedToolsButton;
